import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { useDeleteUserMutation } from '../../mutations';
import { Fragment, useMemo, useState } from 'react';
import { Table } from '~/components/Table/Table';
import { toast } from 'react-toastify';
import { User } from '~/apis/ssf';

interface Props {
  isLoading: boolean;
  users: User[];
}

function Row({ user, onDelete }: { user: User; onDelete: (id: number) => void }) {
  const name = useMemo(() => {
    const parts = [user.first_name, user.last_name].filter(p => p);

    return parts.join(' ');
  }, [user]);

  const permissions = useMemo(() => {
    const p: string[] = [];

    if (user.permissions.federation_admin) {
      p.push('Förbund');
    }

    if (user.permissions.judge_admin) {
      p.push('Domar');
    }

    if (user.permissions.clubs_admin.length > 0) {
      p.push('Förening');
    }

    if (user.permissions.districts_admin.length > 0) {
      p.push('Distrikt');
    }

    if (user.permissions.competitions_admin.length > 0) {
      p.push('Tävling');
    }

    return p.join(', ');
  }, [user]);

  return (
    <Table.Row>
      <Table.Col>{user.username}</Table.Col>
      <Table.Col>{name || '-'}</Table.Col>
      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      <Table.Col>{user.email || '-'}</Table.Col>
      <Table.Col>{permissions || '-'}</Table.Col>
      <Table.Col align="right">
        <Table.Context
          items={[
            <ContextMenu.Item label="Redigera" to={`${user.id}/edit`} key="edit" />,
            <ContextMenu.Item label="Ta bort" onClick={() => onDelete(user.id)} key="delete" danger />,
          ]}
        />
      </Table.Col>
    </Table.Row>
  );
}

export function UserListTable({ users, isLoading }: Props) {
  const deleteMutation = useDeleteUserMutation();

  const [deleteId, setDeleteId] = useState<number>();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(deleteId!), {
      success: 'Användaren har tagits bort',
      pending: 'Tar bort användare...',
    });

    setDeleteId(undefined);
  }

  return (
    <Fragment>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Col as="th">Användarnamn</Table.Col>
            <Table.Col as="th">Namn</Table.Col>
            <Table.Col as="th">E-post</Table.Col>
            <Table.Col as="th">Rättigheter</Table.Col>
            <Table.Col />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading ? (
            <Table.Loading cols={4} />
          ) : (
            users.map((user, index) => <Row user={user} key={index} onDelete={id => setDeleteId(id)} />)
          )}
        </Table.Body>
      </Table>

      <ConfirmDialog
        onClose={() => setDeleteId(undefined)}
        isLoading={deleteMutation.isPending}
        isOpen={deleteId !== undefined}
        onConfirm={handleDelete}
      />
    </Fragment>
  );
}
